import { Button } from "@components/controls/Button"
import { ArrowTopRightIcon } from "@radix-ui/react-icons"
import { formatNiceDateRange, formatNiceTime } from "@utils/dates"
import { EventTicketsFragment } from "../../../types.graphql"
import { PropsWithChildren } from "react"
import { TicketSelector } from "./TicketSelector"
import { useEventTickets } from "@hooks/queries"
import { Spinner } from "@components/atoms/Spinner"
import { ErrorMessage } from "@components/atoms/ErrorMessage"

type Props = {
  data: EventTicketsFragment
}

export function EventRegistrationInfo(props: Props) {
  const data = props.data

  const ticketLookup = useEventTickets(
    {
      id: props.data.id,
    },
    {
      staleTime: 60,
      placeholderData: () => {
        return {
          event: props.data,
        }
      },
    },
  )

  const when = data.dates.length > 0 && (
    <Section label="When" id="when" key="when">
      {data.dates?.map((date, i) => {
        const dateLabel = formatNiceDateRange(date.startDate!, date.endDate)
        const timeLabel =
          date.startTime && date.endTime
            ? formatNiceTime(date.startDate + " " + date.startTime) + "-" + formatNiceTime((date.endDate || date.startDate) + " " + date.endTime)
            : date.startTime
              ? formatNiceTime(date.startDate + " " + date.startTime)
              : ""
        return (
          <div key={i} className="flex justify-between gap-2">
            <div>{dateLabel}</div>
            <div>{timeLabel}</div>
          </div>
        )
      })}
    </Section>
  )

  const where = !!data.eventInfo?.location?.name && (
    <Section label="Where" id="location" key="location">
      <p className="type-body-m-roman">{data.eventInfo?.location.name}</p>
      <p className="type-body-m-roman">{data.eventInfo?.location.address?.replace(data.eventInfo?.location.name, "").replace(/^[\s,]+/gi, "")}</p>
      {!!data.eventInfo?.location.locationNotes && <p className="type-body-s-roman text-grey mt-2">{data.eventInfo?.location.locationNotes}</p>}
      {data.eventInfo.location.address && (
        <div className="mt-4">
          <Button
            href={"https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(data.eventInfo?.location.address)}
            variant="outline"
            rightIcon={<ArrowTopRightIcon />}
          >
            Open in Google Maps
          </Button>
        </div>
      )}
    </Section>
  )

  const pastDate = data.dates?.some((date) => {
    const end = new Date(date.endDate || date.startDate || "")
    end.setHours(23)
    end.setMinutes(59)
    return end < new Date()
  })

  const tickets = ticketLookup.isLoading ? (
    <div className="flex items-center justify-center">
      <Spinner />
    </div>
  ) : ticketLookup.isError ? (
    <ErrorMessage>{ticketLookup.error.message}</ErrorMessage>
  ) : ticketLookup.isSuccess ? (
    ticketLookup.data.event?.ticketProducts?.length! > 0 &&
    !pastDate && (
      <Section label="Tickets" id="tickets" key="tickets">
        <TicketSelector data={ticketLookup.data.event!} eventId={props.data.databaseId} />
      </Section>
    )
  ) : null

  const sections = [when, where, tickets].filter((s) => !!s)

  if (sections.length === 0) return null

  return <div className="border-b">{sections}</div>
}

function Section(props: PropsWithChildren<{ label: string; id?: string }>) {
  return (
    <div className="border-t pt-5 pb-5" id={props.id} data-transition="fade">
      <h3 className="pb-4 type-title-xs-medium">{props.label}</h3>
      {props.children}
    </div>
  )
}
